import type { ReactNode } from 'react';
import { useStore, type View } from '../state/store';
import { formatClock } from '../lib/format';
import { Dot } from './ui';

const GridIcon = (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none" style={{ flex: 'none' }}>
    <rect
      x="2"
      y="2"
      width="7"
      height="7"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="12"
      y="2"
      width="7"
      height="7"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="2"
      y="12"
      width="7"
      height="7"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="12"
      y="12"
      width="7"
      height="7"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const BarsIcon = (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none" style={{ flex: 'none' }}>
    <rect x="2.5" y="11" width="3.5" height="8" rx="1.2" fill="currentColor" />
    <rect x="8.75" y="6" width="3.5" height="13" rx="1.2" fill="currentColor" />
    <rect x="15" y="2.5" width="3.5" height="16.5" rx="1.2" fill="currentColor" />
  </svg>
);

const TerminalIcon = (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none" style={{ flex: 'none' }}>
    <rect
      x="2"
      y="3.5"
      width="17"
      height="14"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <polyline
      points="5.5,8 8,10.5 5.5,13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="10"
      y1="13"
      x2="14.5"
      y2="13"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const AlertIcon = (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none" style={{ flex: 'none' }}>
    <polygon
      points="10.5,2.5 19,17.5 2,17.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <line
      x1="10.5"
      y1="8"
      x2="10.5"
      y2="12.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="10.5" cy="15" r="1" fill="currentColor" />
  </svg>
);

const ServerIcon = (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none" style={{ flex: 'none' }}>
    <rect
      x="2.5"
      y="3"
      width="16"
      height="5.5"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="2.5"
      y="12.5"
      width="16"
      height="5.5"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <circle cx="6" cy="5.75" r="1" fill="currentColor" />
    <circle cx="6" cy="15.25" r="1" fill="currentColor" />
  </svg>
);

const NAV: { view: View; label: string; icon: ReactNode }[] = [
  { view: 'overview', label: 'Overview', icon: GridIcon },
  { view: 'metrics', label: 'Metrics', icon: BarsIcon },
  { view: 'logs', label: 'Logs', icon: TerminalIcon },
  { view: 'alerts', label: 'Alerts', icon: AlertIcon },
  { view: 'server', label: 'Server', icon: ServerIcon },
];

export function Sidebar() {
  const view = useStore((s) => s.view);
  const setView = useStore((s) => s.setView);
  const alerts = useStore((s) => s.alerts);
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  const now = useStore((s) => s.now);
  const streamStatus = useStore((s) => s.streamStatus);
  const system = useStore((s) => s.system);
  const signOut = useStore((s) => s.signOut);
  const retryStream = useStore((s) => s.retryStream);

  const link =
    streamStatus === 'open'
      ? {
          dot: 'var(--pos)',
          ring: 'var(--pos-tint)',
          label: `Bridge live · ${formatClock(now)}`,
        }
      : streamStatus === 'connecting'
        ? { dot: 'var(--faint)', ring: 'transparent', label: 'Reconnecting…' }
        : {
            dot: 'var(--neg)',
            ring: 'var(--neg-tint)',
            label: 'Bridge offline',
          };

  return (
    <div
      className="sidebar"
      style={{
        width: 246,
        flex: 'none',
        background: 'var(--panel)',
        border: '1px solid var(--line)',
        borderRadius: 18,
        boxShadow: 'var(--card)',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 18,
        height: 'calc(100vh - 36px)',
      }}
    >
      <div
        style={{
          padding: '22px 20px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 11,
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            flex: 'none',
            borderRadius: 9,
            background: 'var(--ink)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 21 21"
            fill="none"
            style={{ color: 'var(--panel)' }}
          >
            <rect
              x="2"
              y="2"
              width="7"
              height="7"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.9"
            />
            <rect
              x="12"
              y="2"
              width="7"
              height="7"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.9"
            />
            <rect
              x="2"
              y="12"
              width="7"
              height="7"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.9"
            />
            <rect
              x="12"
              y="12"
              width="7"
              height="7"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.9"
            />
          </svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              fontSize: 16,
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
            }}
          >
            PM2 Console
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--faint)',
              marginTop: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {system?.hostname ?? '—'}
          </div>
        </div>
      </div>

      <nav
        className="sidebar-nav"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: '6px 10px',
        }}
      >
        {NAV.map((item) => {
          const active = view === item.view;
          return (
            <button
              key={item.view}
              type="button"
              className={active ? undefined : 'hoverable'}
              aria-current={active ? 'page' : undefined}
              onClick={() => setView(item.view)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 11,
                height: 42,
                padding: '0 12px',
                borderRadius: 11,
                background: active ? 'var(--page)' : 'transparent',
                color: active ? 'var(--ink)' : 'var(--muted)',
                flex: '1 1 auto',
              }}
            >
              {item.icon}
              <div style={{ fontSize: 14, fontWeight: active ? 600 : 400 }}>
                {item.label}
              </div>
              {item.view === 'alerts' && alerts.length > 0 ? (
                <div
                  className="nav-badge"
                  style={{
                    marginLeft: 'auto',
                    minWidth: 21,
                    height: 21,
                    padding: '0 6px',
                    borderRadius: 999,
                    background: 'var(--neg-tint)',
                    color: 'var(--neg)',
                    fontSize: 11.5,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {alerts.length}
                </div>
              ) : null}
            </button>
          );
        })}
      </nav>

      <div
        className="sidebar-foot"
        style={{
          marginTop: 'auto',
          padding: '16px 20px 18px',
          borderTop: '1px solid var(--line-soft)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            minWidth: 0,
          }}
        >
          <button
            type="button"
            onClick={streamStatus === 'open' ? undefined : retryStream}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: streamStatus === 'open' ? 'default' : 'pointer',
            }}
            title={streamStatus === 'open' ? undefined : 'Reconnect now'}
          >
            <Dot color={link.dot} ring={link.ring} />
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--muted)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {link.label}
            </div>
          </button>
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--faint)',
              wordBreak: 'break-all',
            }}
          >
            {window.location.host}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 4,
            padding: 3,
            borderRadius: 999,
            background: 'var(--page)',
          }}
        >
          {(['light', 'dark'] as const).map((mode) => {
            const on = theme === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setTheme(mode)}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  borderRadius: 999,
                  fontSize: 12.5,
                  textAlign: 'center',
                  background: on ? 'var(--panel)' : 'transparent',
                  color: on ? 'var(--ink)' : 'var(--muted)',
                  boxShadow: on ? 'var(--card)' : 'none',
                  textTransform: 'capitalize',
                }}
              >
                {mode}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="hover-ink"
          onClick={signOut}
          style={{ fontSize: 12, color: 'var(--faint)', textAlign: 'left' }}
        >
          Forget token
        </button>
      </div>
    </div>
  );
}
